
/**
 * Connect core modules and expose globals for compatibility
 * Extracted from MainApp.connectModules for modularization
 */
export function connectCoreModules({
    app,
    filterManager,
    searchEngine,
    fileListManager,
    paginationManager,
    searchUI,
    // typeahead removed
    filesCacheManager,
    languageManager,
    debug = false
}) {
    // Connect filters to search
    filterManager.onFilterChange((filters) => {
        // FilterManager handles its own filtering in applyFilters()
    });

    // Connect file list to pagination
    fileListManager.onFilesUpdate((files) => {
        paginationManager.setTotalItems(files.length);
    });

    // Connect pagination to file list
    paginationManager.onPageChange((page, pageSize) => {
        fileListManager.setPage(page, pageSize);
    });


    // Export core modules globally for backward compatibility
    window.searchEngine = searchEngine;
    window.fileListManager = fileListManager;
    window.searchUI = searchUI;
    window.pagination = paginationManager;
    // typeahead removed

    // Export key modules globally for final-system-check and compatibility
    window.filterManager = filterManager;
    window.paginationManager = paginationManager;
    window.languageManager = languageManager;

    // Lightweight updater to refresh files when server changes
    window.refreshFilesFromServer = async () => {
        try {
            // Get user country for restriction filtering
            let userCountry = null;
            try {
                const countryResponse = await fetch('/api/files/user-country');
                if (countryResponse.ok) {
                    const countryData = await countryResponse.json();
                    if (countryData && countryData.data && countryData.data.country) {
                        userCountry = countryData.data.country;
                        try { sessionStorage.setItem('userCountry', userCountry); } catch(_) {}
                    }
                }
            } catch (e) {
                try { userCountry = sessionStorage.getItem('userCountry'); } catch(_) {}
            }
            
            const res = await filesCacheManager.loadFiles({
                url: '/api/files/',
                forceUpdate: true,
                processFiles: true,
                processingOptions: {
                    sanitize: true,
                    filterConfidential: !(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated),
                    filterCountryRestrictions: true,
                    userCountry: userCountry
                }
            });
            if (res && Array.isArray(res.files) && res.files.length) {
                if (window.filesStore && typeof window.filesStore.setFiles === 'function') {
                    window.filesStore.setFiles(res.files);
                }
                fileListManager.setFiles(res.files);
                searchEngine.buildIndex(res.files);
                app.lastFilesTimestamp = res.timestamp || Date.now();
                
                // Update filters after files refresh
                try {
                    // Wait a bit for filter_metadata to be generated and saved
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Load filter metadata from IDB and refresh filter UI
                    const { openDatabase, closeDatabase } = await import('../storage/idb-files.js');
                    const db = await openDatabase();
                    if (db) {
                        try {
                            // Get filter_metadata directly from meta store
                            const tx = db.transaction('meta', 'readonly');
                            const store = tx.objectStore('meta');
                            const filterMeta = await new Promise((resolve, reject) => {
                                const filterMetaReq = store.get('filter_metadata');
                                filterMetaReq.onsuccess = () => resolve(filterMetaReq.result);
                                filterMetaReq.onerror = () => reject(filterMetaReq.error || new Error('Failed to read filter_metadata'));
                            });
                            
                            if (filterMeta && filterMeta.uniqueValues) {
                                if (window.HyperLog) {
                                    window.HyperLog.debug('[MainApp] Loaded filter metadata from IDB', {
                                        hasFilterMeta: !!filterMeta,
                                        hasUniqueValues: !!(filterMeta && filterMeta.uniqueValues),
                                        hasFilterController: !!window.filterController,
                                        filterMetaKeys: filterMeta ? Object.keys(filterMeta) : []
                                    });
                                }
                                
                                // Refresh filters with new metadata (includes updated OT files)
                                if (window.filterController && typeof window.filterController.refreshFiltersFromMetadata === 'function') {
                                    if (!window.filterController._isRefreshing) {
                                        window.filterController.refreshFiltersFromMetadata(filterMeta);
                                        if (window.HyperLog) {
                                            window.HyperLog.info('[MainApp] Filters refreshed after files update');
                                        }
                                    }
                                }
                            }
                        } finally {
                            closeDatabase(db);
                        }
                    }
                } catch (filterErr) {
                    if (window.HyperLog) {
                        window.HyperLog.warn('[MainApp] Failed to update filters after refresh', { error: filterErr?.message });
                    }
                }
                
                if (window.HyperLog) {
                    window.HyperLog.info('[MainApp] Files refreshed from server');
                }
            }
        } catch (e) { /* noop */ }
    };

}


